// TEMPORARY MOCK ADAPTER for Questionnaire Design (PRD 05 / 문항 설계).
// In-memory only — state lives in this module's `questionStore` and resets on
// hard reload. Mutations resolve with ~200 ms simulated latency. Errors mimic
// the BE envelope (QuestionnaireDesignApiError).
// Backend switch: VITE_USE_MOCK_API=false flips to the real HTTP adapter; see
// questionnaire-design.adapter.ts.
//
// Seed: 23 questions across 7 of the 13 PRO items, ported from
// prototype/pages/survey-editor.html `questionsMap`. The Korean prototype
// values are mapped to the SCREAMING_SNAKE wire enums used here.

import { proTreeMockApi } from '@/features/pro-tree/api/pro-tree.mock.api';
import type { ProTreeNode } from '@/features/pro-tree/types/pro-tree.types';

import {
  QuestionnaireDesignApiError,
  type CopyQuestionInput,
  type CreateQuestionInput,
  type Question,
  type QuestionnaireDesignApi,
  type UpdateQuestionInput,
} from '../types/questionnaire-design.types';

const LATENCY_MS = 200;

const clone = <T>(value: T): T => JSON.parse(JSON.stringify(value));
const delay = (ms: number = LATENCY_MS): Promise<void> =>
  new Promise((resolve) => setTimeout(resolve, ms));

// ── QID generator ────────────────────────────────────────────────────────
// Starts at 22 (matches prototype seed range Q-0001..Q-0022). The first
// generated id is Q-0023.
let qSerial = 22;
function nextQid(): string {
  qSerial += 1;
  return `Q-${String(qSerial).padStart(4, '0')}`;
}

const nowIso = (): string => new Date().toISOString();

// ── Seed (port from prototype `questionsMap`) ────────────────────────────
//
// Schema differences vs the prototype object:
//   - `id`/`serial` → `qid` (single canonical id).
//   - `target` / `gender` / `applicability` → SCREAMING_SNAKE enums.
//   - `branch` strings ('→ 다음 문항', '→ 문항 종료', '→ 문항 1-1') →
//     BranchRef { kind, targetQid? }. Position-based prototype jumps without a
//     QID anchor are downgraded to NEXT here — the production BE owns true
//     cross-question linking.
//   - `children` removed; sub-questions are flat rows with `parentQid` set.

const seed: Question[] = [
  // item-1-1-1 — 지속적인 우울감
  {
    qid: 'Q-0001',
    itemId: 'item-1-1-1',
    parentQid: null,
    text: '최근 1주일 동안 귀하의 건강과 관련된 질문입니다. 보기를 읽고 귀하의 상태를 가장 잘 표현하는 것을 선택하여 주십시오.',
    target: 'PATIENT',
    gender: 'COMMON',
    applicability: 'COMMON',
    answerType: 'SHORT_ANSWER',
    allowPhoto: false,
    choices: [
      { text: '나는 전혀 우울하지 않았다', score: 0, branch: { kind: 'END' } },
      { text: '나는 가끔 우울했다', score: 1, branch: { kind: 'END' } },
      { text: '나는 자주 우울했다', score: 2, branch: { kind: 'END' } },
      { text: '나는 항상 우울했다', score: 2, branch: { kind: 'END' } },
    ],
    advanced: { scoreContribution: true, weight: 1.0, reliability: 1.0, min: 0, max: 3 },
    order: 0,
    createdAt: nowIso(),
    updatedAt: nowIso(),
  },
  // item-1-1-2 — 자살 관련 행동 (parent + 4 sub-questions)
  {
    qid: 'Q-0002',
    itemId: 'item-1-1-2',
    parentQid: null,
    text: '본인 스스로를 정말 해치겠다는 생각을 했던 적이 있습니까?',
    target: 'PATIENT',
    gender: 'COMMON',
    applicability: 'COMMON',
    answerType: 'YES_NO',
    allowPhoto: false,
    // Original prototype jumps to '문항 1-1' — anchor preserved as JUMP→Q-0003.
    choices: [
      { text: '있다', score: 1, branch: { kind: 'JUMP', targetQid: 'Q-0003' } },
      { text: '없다', score: 0, branch: { kind: 'END' } },
    ],
    advanced: { scoreContribution: true, weight: 1.0, reliability: 1.0, min: 0, max: 10 },
    order: 0,
    createdAt: nowIso(),
    updatedAt: nowIso(),
  },
  {
    qid: 'Q-0003',
    itemId: 'item-1-1-2',
    parentQid: 'Q-0002',
    text: '이전에 본인 스스로를 해치려는 행동을 한 적 있습니까?',
    target: 'PATIENT',
    gender: 'COMMON',
    applicability: 'COMMON',
    answerType: 'YES_NO',
    allowPhoto: false,
    choices: [
      { text: '있다', score: 2, branch: { kind: 'NEXT' } },
      { text: '없다', score: 0, branch: { kind: 'NEXT' } },
    ],
    advanced: { scoreContribution: true, weight: 1.0, reliability: 1.0, min: 0, max: 10 },
    order: 0,
    createdAt: nowIso(),
    updatedAt: nowIso(),
  },
  {
    qid: 'Q-0004',
    itemId: 'item-1-1-2',
    parentQid: 'Q-0002',
    text: '본인 스스로를 정말 해칠 방법에 대해 지금도 생각을 하고 있습니까?',
    target: 'PATIENT',
    gender: 'COMMON',
    applicability: 'COMMON',
    answerType: 'YES_NO',
    allowPhoto: false,
    choices: [
      { text: '있다', score: 2, branch: { kind: 'NEXT' } },
      { text: '없다', score: 0, branch: { kind: 'NEXT' } },
    ],
    advanced: { scoreContribution: true, weight: 1.0, reliability: 1.0, min: 0, max: 10 },
    order: 1,
    createdAt: nowIso(),
    updatedAt: nowIso(),
  },
  {
    qid: 'Q-0005',
    itemId: 'item-1-1-2',
    parentQid: 'Q-0002',
    text: '앞으로 1달 이내에 당신 스스로를 해치거나 삶을 끝내겠다는 생각을 행동으로 옮길 것 같습니까?',
    target: 'PATIENT',
    gender: 'COMMON',
    applicability: 'COMMON',
    answerType: 'SHORT_ANSWER',
    allowPhoto: false,
    choices: [
      { text: '전혀 아니다', score: 0, branch: { kind: 'NEXT' } },
      { text: '약간 그렇다', score: 2, branch: { kind: 'NEXT' } },
      { text: '매우 그렇다', score: 3, branch: { kind: 'NEXT' } },
    ],
    advanced: { scoreContribution: true, weight: 1.0, reliability: 0.9, min: 0, max: 10 },
    order: 2,
    createdAt: nowIso(),
    updatedAt: nowIso(),
  },
  {
    qid: 'Q-0006',
    itemId: 'item-1-1-2',
    parentQid: 'Q-0002',
    text: '당신 자신을 해치려는 행동을 멈추게 하거나 하지 못하게 막는 것이 있습니까?',
    target: 'PATIENT',
    gender: 'COMMON',
    applicability: 'COMMON',
    answerType: 'YES_NO',
    allowPhoto: false,
    choices: [
      { text: '있다', score: 0, branch: { kind: 'END' } },
      { text: '없다', score: 1, branch: { kind: 'END' } },
    ],
    advanced: { scoreContribution: true, weight: 1.0, reliability: 1.0, min: 0, max: 10 },
    order: 3,
    createdAt: nowIso(),
    updatedAt: nowIso(),
  },
  // item-1-2-1 — 복약 상태 (parent + 2 sub-questions)
  {
    qid: 'Q-0015',
    itemId: 'item-1-2-1',
    parentQid: null,
    text: '어제 처방받은 약을 규칙적으로 복용하셨습니까?',
    target: 'PATIENT',
    gender: 'COMMON',
    applicability: 'COMMON',
    answerType: 'YES_NO',
    allowPhoto: false,
    choices: [
      { text: '예', score: 0, branch: { kind: 'JUMP', targetQid: 'Q-0016' } },
      { text: '아니오', score: 3, branch: { kind: 'JUMP', targetQid: 'Q-0017' } },
    ],
    advanced: { scoreContribution: true, weight: 1.0, reliability: 1.0, min: 0, max: 10 },
    order: 0,
    createdAt: nowIso(),
    updatedAt: nowIso(),
  },
  {
    qid: 'Q-0016',
    itemId: 'item-1-2-1',
    parentQid: 'Q-0015',
    text: '얼마나 복용하였습니까?',
    target: 'PATIENT',
    gender: 'COMMON',
    applicability: 'COMMON',
    answerType: 'SHORT_ANSWER',
    allowPhoto: false,
    choices: [
      { text: '전부', score: 0, branch: { kind: 'NEXT' } },
      { text: '대부분', score: 1, branch: { kind: 'NEXT' } },
      { text: '반정도', score: 1, branch: { kind: 'NEXT' } },
      { text: '조금', score: 3, branch: { kind: 'NEXT' } },
      { text: '안함', score: 3, branch: { kind: 'NEXT' } },
    ],
    advanced: { scoreContribution: true, weight: 1.0, reliability: 1.0, min: 0, max: 10 },
    order: 0,
    createdAt: nowIso(),
    updatedAt: nowIso(),
  },
  {
    qid: 'Q-0017',
    itemId: 'item-1-2-1',
    parentQid: 'Q-0015',
    text: '복용하지 못한 이유는?',
    target: 'PATIENT',
    gender: 'COMMON',
    applicability: 'COMMON',
    answerType: 'MULTI_CHOICE',
    allowPhoto: false,
    choices: [
      { text: '잊어버림', score: 3 },
      { text: '부작용', score: 3 },
      { text: '다 나았다', score: 3 },
      { text: '효과없음', score: 3 },
      { text: '비용', score: 3 },
      { text: '시간없음', score: 3 },
      { text: '복잡', score: 3 },
      { text: '기타', score: 3 },
    ],
    advanced: { scoreContribution: true, weight: 0.9, reliability: 1.0, min: 0, max: 10 },
    order: 1,
    createdAt: nowIso(),
    updatedAt: nowIso(),
  },
  // item-1-3-1 — 불면·과수면 (7 short-answer questions, all top-level)
  ...['Q-0007', 'Q-0008', 'Q-0009', 'Q-0010', 'Q-0011', 'Q-0012', 'Q-0013'].map(
    (qid, idx): Question => {
      const texts = [
        '잠들기 어려운 정도를 선택해 주십시오.',
        '잠을 유지하기 어려운 정도를 선택해 주십시오.',
        '새벽에 너무 일찍 잠에서 깨는 정도를 선택해 주십시오.',
        '수면 패턴 만족정도를 선택해 주십시오.',
        '불면으로 인한 삶의 질 손상정도를 선택해 주십시오.',
        '불면증에 대한 걱정 정도를 선택해 주십시오.',
        '수면문제로 인한 일상생활 방해정도를 선택해 주십시오.',
      ];
      const reliabilities = [0.9, 0.9, 0.9, 0.8, 0.7, 0.8, 0.8];
      const lastBranchKind = idx === 6 ? ('END' as const) : ('NEXT' as const);
      return {
        qid,
        itemId: 'item-1-3-1',
        parentQid: null,
        text: texts[idx],
        target: 'PATIENT',
        gender: 'COMMON',
        applicability: 'COMMON',
        answerType: 'SHORT_ANSWER',
        allowPhoto: false,
        choices: [
          { text: '전혀없음', score: 0, branch: { kind: lastBranchKind } },
          { text: '약간있음', score: 1, branch: { kind: lastBranchKind } },
          { text: '중간', score: 2, branch: { kind: lastBranchKind } },
          { text: '심함', score: 3, branch: { kind: lastBranchKind } },
          { text: '매우심함', score: 4, branch: { kind: lastBranchKind } },
        ],
        advanced: {
          scoreContribution: true,
          weight: 0.3,
          reliability: reliabilities[idx],
          min: 0,
          max: 4,
        },
        order: idx,
        createdAt: nowIso(),
        updatedAt: nowIso(),
      };
    },
  ),
  // item-2-2-1 — 규칙적인 식사
  {
    qid: 'Q-0014',
    itemId: 'item-2-2-1',
    parentQid: null,
    text: '최근 1주 동안의 자기 상태를 가장 잘 나타내는 설명을 선택하십시오. (식욕의 변화)',
    target: 'PATIENT',
    gender: 'COMMON',
    applicability: 'COMMON',
    answerType: 'SHORT_ANSWER',
    allowPhoto: false,
    choices: [
      { text: '나의 식욕은 예전과 변함이 없다', score: 0, branch: { kind: 'END' } },
      { text: '나는 예전보다 식욕이 조금 줄었다', score: 1, branch: { kind: 'END' } },
      { text: '나는 예전보다 식욕이 조금 늘었다', score: 1, branch: { kind: 'END' } },
      { text: '나는 예전보다 식욕이 많이 줄었다', score: 2, branch: { kind: 'END' } },
      { text: '나는 예전보다 식욕이 많이 늘었다', score: 2, branch: { kind: 'END' } },
      { text: '나는 식욕이 전혀 없다', score: 3, branch: { kind: 'END' } },
      { text: '나는 항상 음식이 몹시 먹고 싶다', score: 3, branch: { kind: 'END' } },
    ],
    advanced: { scoreContribution: true, weight: 1.0, reliability: 1.0, min: 0, max: 3 },
    order: 0,
    createdAt: nowIso(),
    updatedAt: nowIso(),
  },
  // item-4-2-1 — 일상관리 지속 의지 (BATTERY)
  {
    qid: 'Q-SES-01',
    itemId: 'item-4-2-1',
    parentQid: null,
    text: '자기효능감 척도 (K-SRAHP)',
    target: 'PATIENT',
    gender: 'COMMON',
    applicability: 'COMMON',
    answerType: 'BATTERY',
    allowPhoto: false,
    batteryConfig: {
      scaleLabels: ['전혀 그렇지 않다', '그렇지 않다', '그렇다', '매우 그렇다'],
      scaleScores: [0, 1, 2, 3],
      categories: [
        {
          name: '운동관리',
          items: [
            { text: '나는 나에게 유익한 운동을 할 수 있다', allowPhoto: false },
            { text: '나는 규칙적으로 운동을 지속할 수 있다', allowPhoto: false },
            { text: '나는 적당한 운동을 할 수 있다', allowPhoto: false },
          ],
        },
        {
          name: '심리적 안녕',
          items: [
            { text: '나는 기분이 나쁠 때 기분전환 할 수 있는 방법을 알고 있다', allowPhoto: false },
            { text: '나는 내 감정을 적절히 표현할 수 있다', allowPhoto: false },
            { text: '나는 내가 원하는 것을 이루기 위해 노력할 수 있다', allowPhoto: false },
            { text: '나는 내 자신을 있는 그대로 받아들일 수 있다', allowPhoto: false },
          ],
        },
        {
          name: '건강에 관한 책임감',
          items: [
            { text: '나는 건강에 관한 정보를 이해할 수 있다', allowPhoto: false },
            { text: '나는 건강에 해로운 습관을 고칠 수 있다', allowPhoto: false },
            { text: '나는 몸에 이상이 있을 때 의료기관을 방문할 수 있다', allowPhoto: false },
            { text: '나는 내가 복용하는 약의 목적을 이해할 수 있다', allowPhoto: false },
            { text: '나는 건강검진을 정기적으로 받을 수 있다', allowPhoto: false },
          ],
        },
        {
          name: '영양관리',
          items: [
            { text: '나는 어떤 음식에 섬유소가 많이 들어있는지 말할 수 있다', allowPhoto: true },
            { text: '나는 어떤 음식이 나에게 좋은지 라벨을 통해 알아낼 수 있다', allowPhoto: false },
            { text: '나는 균형 잡힌 식사를 할 수 있다', allowPhoto: false },
            { text: '나는 건강에 좋은 간식을 선택할 수 있다', allowPhoto: false },
          ],
        },
      ],
    },
    advanced: { scoreContribution: true, weight: 1.0, reliability: 1.0, min: 16, max: 64 },
    order: 0,
    createdAt: nowIso(),
    updatedAt: nowIso(),
  },
  // item-5-1-1 — 불면증
  {
    qid: 'Q-0021',
    itemId: 'item-5-1-1',
    parentQid: null,
    text: '지난 1주일간 잠들기 어려웠습니까?',
    target: 'PATIENT',
    gender: 'COMMON',
    applicability: 'DISEASE_SPECIFIC',
    diseaseCodeId: 104, // F32 우울 에피소드 (matches disease-code mock id)
    answerType: 'SHORT_ANSWER',
    allowPhoto: false,
    choices: [
      { text: '전혀 아니다', score: 0, branch: { kind: 'END' } },
      { text: '가끔', score: 1, branch: { kind: 'END' } },
      { text: '자주', score: 2, branch: { kind: 'END' } },
      { text: '거의 매일', score: 3, branch: { kind: 'END' } },
    ],
    advanced: { scoreContribution: true, weight: 1.0, reliability: 0.9, min: 0, max: 3 },
    order: 0,
    createdAt: nowIso(),
    updatedAt: nowIso(),
  },
  {
    qid: 'Q-0022',
    itemId: 'item-5-1-1',
    parentQid: null,
    text: '지난 1주일간 환자의 수면 상태는 어떠했습니까?',
    target: 'CAREGIVER',
    gender: 'COMMON',
    applicability: 'DISEASE_SPECIFIC',
    diseaseCodeId: 104,
    answerType: 'SHORT_ANSWER',
    allowPhoto: false,
    choices: [
      { text: '양호', score: 0, branch: { kind: 'END' } },
      { text: '가끔 불편해함', score: 1, branch: { kind: 'END' } },
      { text: '자주 깨거나 뒤척임', score: 2, branch: { kind: 'END' } },
      { text: '거의 매일 수면 곤란', score: 3, branch: { kind: 'END' } },
    ],
    advanced: { scoreContribution: true, weight: 1.0, reliability: 0.9, min: 0, max: 3 },
    order: 0,
    createdAt: nowIso(),
    updatedAt: nowIso(),
  },
  // item-2-5-1 — 재활운동 수행 (parent + 1 sub + sibling)
  {
    qid: 'Q-0018',
    itemId: 'item-2-5-1',
    parentQid: null,
    text: '오늘 재활운동을 하셨습니까?',
    target: 'PATIENT',
    gender: 'COMMON',
    applicability: 'DISEASE_SPECIFIC',
    diseaseCodeId: 305, // I63 뇌경색
    answerType: 'YES_NO',
    allowPhoto: false,
    choices: [
      { text: '예', score: 1, branch: { kind: 'END' } },
      { text: '아니오', score: 0, branch: { kind: 'JUMP', targetQid: 'Q-0019' } },
    ],
    advanced: { scoreContribution: true, weight: 1.0, reliability: 1.0, min: 0, max: 1 },
    order: 0,
    createdAt: nowIso(),
    updatedAt: nowIso(),
  },
  {
    qid: 'Q-0019',
    itemId: 'item-2-5-1',
    parentQid: 'Q-0018',
    text: '재활운동을 하지 못한 이유는 무엇입니까?',
    target: 'PATIENT',
    gender: 'COMMON',
    applicability: 'DISEASE_SPECIFIC',
    diseaseCodeId: 305,
    answerType: 'SHORT_ANSWER',
    allowPhoto: false,
    choices: [
      { text: '통증이 심해서', score: 0, branch: { kind: 'END' } },
      { text: '피로해서', score: 0, branch: { kind: 'END' } },
      { text: '방법을 잘 몰라서', score: 0, branch: { kind: 'END' } },
      { text: '의욕이 없어서', score: 0, branch: { kind: 'END' } },
      { text: '기타', score: 0, branch: { kind: 'END' } },
    ],
    advanced: { scoreContribution: true, weight: 1.0, reliability: 1.0, min: 0, max: 0 },
    order: 0,
    createdAt: nowIso(),
    updatedAt: nowIso(),
  },
  {
    qid: 'Q-0020',
    itemId: 'item-2-5-1',
    parentQid: null,
    text: '오늘 환자가 재활운동에 참여했습니까?',
    target: 'CAREGIVER',
    gender: 'COMMON',
    applicability: 'DISEASE_SPECIFIC',
    diseaseCodeId: 305,
    answerType: 'YES_NO',
    allowPhoto: false,
    choices: [
      { text: '예', score: 1, branch: { kind: 'END' } },
      { text: '아니오', score: 0, branch: { kind: 'END' } },
    ],
    advanced: { scoreContribution: true, weight: 1.0, reliability: 1.0, min: 0, max: 1 },
    order: 0,
    createdAt: nowIso(),
    updatedAt: nowIso(),
  },
];

// ── In-memory store ──────────────────────────────────────────────────────

const questionStore: Question[] = [...seed];

function findQuestionOrThrow(qid: string): Question {
  const q = questionStore.find((row) => row.qid === qid);
  if (!q) {
    throw new QuestionnaireDesignApiError(
      'NOT_FOUND',
      '해당 문항을 찾을 수 없습니다.',
    );
  }
  return q;
}

function nextOrderFor(itemId: string, target: Question['target'], parentQid: string | null): number {
  return questionStore.filter(
    (row) =>
      row.itemId === itemId &&
      row.target === target &&
      (row.parentQid ?? null) === parentQid,
  ).length;
}

// ── API ──────────────────────────────────────────────────────────────────

export const questionnaireDesignMockApi: QuestionnaireDesignApi = {
  async listProTree(): Promise<ProTreeNode[]> {
    // Delegate to the shared PRO-tree mock so PRD 04/05/06 stay aligned.
    return proTreeMockApi.listProTree();
  },

  async listAllQuestions(): Promise<Question[]> {
    await delay();
    return clone(questionStore);
  },

  async listQuestions(itemId: string): Promise<Question[]> {
    await delay();
    return clone(questionStore.filter((q) => q.itemId === itemId));
  },

  async createQuestion(input: CreateQuestionInput): Promise<Question> {
    await delay();
    const parentQid = input.parentQid ?? null;
    if (parentQid) {
      const parent = findQuestionOrThrow(parentQid);
      if (parent.parentQid) {
        throw new QuestionnaireDesignApiError(
          'MAX_DEPTH_EXCEEDED',
          '하위 문항은 메인 문항 아래 1단계까지만 생성 가능합니다.',
        );
      }
    }
    const now = nowIso();
    const created: Question = {
      qid: nextQid(),
      itemId: input.itemId,
      parentQid,
      text: input.text,
      target: input.target,
      gender: input.gender,
      applicability: input.applicability,
      diseaseGroupId: input.diseaseGroupId ?? null,
      diseaseCodeId: input.diseaseCodeId ?? null,
      answerType: input.answerType,
      allowPhoto: input.allowPhoto ?? false,
      choices: input.choices,
      multiConfig: input.multiConfig,
      textConfig: input.textConfig,
      sliderConfig: input.sliderConfig,
      photoConfig: input.photoConfig,
      batteryConfig: input.batteryConfig,
      advanced: input.advanced,
      order: nextOrderFor(input.itemId, input.target, parentQid),
      createdAt: now,
      updatedAt: now,
    };
    questionStore.push(created);
    return clone(created);
  },

  async updateQuestion(qid: string, input: UpdateQuestionInput): Promise<Question> {
    await delay();
    const target = findQuestionOrThrow(qid);
    Object.assign(target, input, { updatedAt: nowIso() });
    return clone(target);
  },

  async deleteQuestion(qid: string): Promise<void> {
    await delay();
    findQuestionOrThrow(qid); // ensure exists
    // Cascade: remove the question + all sub-questions.
    const toRemove = new Set<string>([qid]);
    for (const row of questionStore) {
      if (row.parentQid === qid) toRemove.add(row.qid);
    }
    for (let i = questionStore.length - 1; i >= 0; i -= 1) {
      if (toRemove.has(questionStore[i].qid)) questionStore.splice(i, 1);
    }
  },

  async copyQuestion(qid: string, input: CopyQuestionInput): Promise<Question> {
    await delay();
    const src = findQuestionOrThrow(qid);
    const now = nowIso();
    const newQid = nextQid();
    // PRD §3.8.3 / §3.8.4 — body + answer structure + Config + allowPhoto are
    // copied; branches reset to NEXT; meta blanked except `target`;
    // scoreContribution forced ON.
    // SHCT-862 — the 복사하여 하위문항추가 entry point sends the parent to attach under; the 컬럼 헤더
    // one sends nothing and the copy stays top level (PRD 05 v1.34 §3.8.1).
    const destParentQid = input.targetParentQid ?? null;
    const copied: Question = {
      qid: newQid,
      itemId: input.targetItemId,
      parentQid: destParentQid,
      text: src.text,
      target: input.targetColumn,
      gender: 'COMMON',
      applicability: 'COMMON',
      diseaseGroupId: null,
      diseaseCodeId: null,
      answerType: src.answerType,
      allowPhoto: src.allowPhoto,
      choices: src.choices?.map((c) => ({
        ...clone(c),
        branch: c.branch ? { kind: 'NEXT' } : undefined,
      })),
      multiConfig: src.multiConfig ? clone(src.multiConfig) : undefined,
      textConfig: src.textConfig ? clone(src.textConfig) : undefined,
      sliderConfig: src.sliderConfig ? clone(src.sliderConfig) : undefined,
      photoConfig: src.photoConfig ? clone(src.photoConfig) : undefined,
      batteryConfig: src.batteryConfig ? clone(src.batteryConfig) : undefined,
      advanced: { ...src.advanced, scoreContribution: true },
      order: nextOrderFor(input.targetItemId, input.targetColumn, destParentQid),
      createdAt: now,
      updatedAt: now,
    };
    questionStore.push(copied);
    return clone(copied);
  },
};
